import { Link } from 'react-router-dom';
import { StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 Closeout / Handoff Readiness Center (PRD CO-001…CO-072).
 * Mirrors the as-built Diagnosis/Metrics/Governance view-switcher: chips set an active view; inactive
 * views stay mounted via `hidden`; an Expand all / Collapse toggle (sibling OUTSIDE the tablist,
 * `aria-pressed`) reveals every view. The owning CloseoutTab keeps the stateful handlers (validate,
 * handoff-owner, checklist toggles) and passes data + callbacks in, so this module stays presentational.
 * Checklist grouping is VISUAL ONLY — each item carries its original full-list index for stable testids.
 */

export const CLOSEOUT_SECTIONS = [
  { key: 'readiness', label: 'Readiness' },
  { key: 'validation', label: 'Validation & Sign-off' },
  { key: 'checklist', label: 'Checklist' },
  { key: 'handoff-packet', label: 'Handoff Packet' },
  { key: 'graduation-lineage', label: 'Graduation & Lineage' },
  { key: 'lessons', label: 'Lessons' },
];

const VALIDATION_OPTIONS = [
  ['Accepted', 'closeout-validation-accepted', 'Sponsor accepts the closeout package as ready.'],
  [
    'Accepted with follow-up',
    'closeout-validation-accepted-followup',
    'Package can proceed with tracked follow-up.',
  ],
  ['Not accepted / gaps remain', 'closeout-validation-not-accepted', 'Gaps remain; not accepted.'],
  ['Routed to another process', 'closeout-validation-routed', 'Closed into another process.'],
  ['Graduated to project', 'closeout-validation-graduated', 'Project-preview path selected.'],
];

const NON_TOGGLE_ITEMS = new Set(['Sponsor validation recorded', 'Handoff owner assigned']);

// ---------------------------------------------------------------------------
// Derivations
// ---------------------------------------------------------------------------

// Required gates open = requiredForUnblock && !done (baseline 5). "Sponsor validation recorded" is
// already one of them — never add the validation status as a separate gate.
export function deriveRequiredGatesOpen(checklist = []) {
  return checklist.filter((item) => item.requiredForUnblock && !item.done);
}

export function deriveLessonId(text = '') {
  const match = String(text).match(/^(LL-\d+)/i);
  return match ? match[1] : null;
}

function slug(value = '') {
  return String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Partition the checklist (each item carrying { originalIndex }) into the four visual groups.
export function groupCloseoutChecklist(checklist = []) {
  const withIndex = checklist.map((item, originalIndex) => ({ ...item, originalIndex }));
  return {
    requiredOpen: withIndex.filter((i) => i.requiredForUnblock && !i.done),
    requiredComplete: withIndex.filter((i) => i.requiredForUnblock && i.done),
    supportingComplete: withIndex.filter((i) => !i.requiredForUnblock && i.done),
    openNonBlocking: withIndex.filter((i) => !i.requiredForUnblock && !i.done),
  };
}

// ---------------------------------------------------------------------------
// View-switcher nav (Expand all OUTSIDE the tablist)
// ---------------------------------------------------------------------------

export function CloseoutSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="closeout-section-nav" data-testid="closeout-section-nav">
      <div className="closeout-nav-tabs" role="tablist" aria-label="Closeout sections">
        {CLOSEOUT_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`closeout-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`closeout-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`closeout-nav-chip closeout-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="closeout-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'readiness' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// 7.1 Always-on Handoff Readiness Summary
// ---------------------------------------------------------------------------

export function CloseoutReadinessSummary({ summary }) {
  const { canClose } = summary;
  return (
    <section
      className="rapid-panel closeout-readiness-summary"
      data-testid="closeout-readiness-summary"
    >
      <div className="closeout-summary-head">
        <div className="eyebrow">Handoff readiness</div>
        <h3 className="closeout-summary-headline">
          <span data-testid="closeout-summary-verdict">
            {canClose ? 'Ready to close and hand off' : 'Closeout blocked'}
          </span>
        </h3>
      </div>

      <div className="closeout-summary-stats">
        <div className={`closeout-summary-stat is-primary ${canClose ? 'is-ready' : 'is-blocked'}`}>
          <span className="k">Can close today?</span>
          <span className="v is-text" data-testid="closeout-summary-can-close">
            {canClose ? 'Yes' : 'No'}
          </span>
          <span className="sub">{canClose ? 'gates cleared' : 'required gates remain'}</span>
        </div>
        <div className="closeout-summary-stat">
          <span className="k">Readiness</span>
          <span className="v" data-testid="closeout-summary-readiness-percent">
            {summary.percent}% complete
          </span>
          <span className="sub" data-testid="closeout-summary-complete-count">
            {summary.complete} / {summary.total}
          </span>
        </div>
        <div className="closeout-summary-stat">
          <span className="k">Required gates open</span>
          <span className="v" data-testid="closeout-summary-required-gates-open">
            {summary.requiredGatesOpen}
          </span>
          <span className="sub" data-testid="closeout-summary-sponsor-validation">
            Validation: {summary.sponsorValidation}
          </span>
        </div>
        <div className="closeout-summary-stat">
          <span className="k">Handoff owner</span>
          <span className="v is-text" data-testid="closeout-summary-handoff-owner">
            {summary.handoffOwner || 'Owner needed'}
          </span>
          <span className="sub" data-testid="closeout-summary-sustainment-cadence">
            {summary.sustainmentCadence}
          </span>
        </div>
      </div>

      <div className="closeout-summary-next" data-testid="closeout-summary-next-move">
        <span className="chip outline sm">Next required move</span>
        <p>{summary.nextMove}</p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.3 Readiness view
// ---------------------------------------------------------------------------

export function CloseoutReadinessView({ canClose, blockers, readyItems, nextMove, onOpenView }) {
  return (
    <section className="rapid-panel closeout-readiness-view" data-testid="closeout-readiness-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Readiness</div>
          <h3>Can this RAPID close today?</h3>
        </div>
      </div>

      <div className="closeout-can-close" data-testid="closeout-can-close">
        <StatusChip status={canClose ? 'done' : 'open'}>
          {canClose ? 'Yes — ready to hand off' : 'No — required gates remain open'}
        </StatusChip>
      </div>

      <div className="closeout-readiness-grid">
        <div
          className="closeout-readiness-col is-blocking"
          data-testid="closeout-blocking-gates-summary"
        >
          <h4>What blocks closeout ({blockers.length})</h4>
          {blockers.length ? (
            <ul className="rapid-inline-list">
              {blockers.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : (
            <p className="muted small">Nothing blocking — required gates are clear.</p>
          )}
          <button
            type="button"
            className="btn ghost sm"
            data-testid="closeout-goto-validation"
            onClick={() => onOpenView('validation')}
          >
            Go to Validation &amp; Sign-off
          </button>
        </div>
        <div className="closeout-readiness-col is-ready" data-testid="closeout-ready-items-summary">
          <h4>What is ready ({readyItems.length})</h4>
          <ul className="rapid-inline-list">
            {readyItems.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <button
            type="button"
            className="btn ghost sm"
            data-testid="closeout-goto-checklist"
            onClick={() => onOpenView('checklist')}
          >
            Go to Checklist
          </button>
        </div>
      </div>

      <div className="closeout-next-move" data-testid="closeout-next-required-move">
        <span className="chip outline sm">Next move</span>
        <p>{nextMove}</p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.4 Validation & Sign-off view (preserves all real controls + literal notes)
// ---------------------------------------------------------------------------

export function CloseoutValidationView({
  validationStatus,
  handoffOwner,
  onHandoffOwnerChange,
  onValidate,
  validating,
  routedDestination,
  blocked,
  notAccepted,
  ownerMissing,
  requiredRemaining,
}) {
  return (
    <section
      className="rapid-panel closeout-validation-view"
      data-testid="closeout-validation-view"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Validation &amp; sign-off</div>
          <h3>Sponsor decision</h3>
        </div>
        <StatusChip status={validationStatus}>
          <span data-testid="closeout-validation-status">{validationStatus}</span>
        </StatusChip>
      </div>

      <label className="rapid-field" data-testid="closeout-handoff-owner-field">
        <span>Handoff owner</span>
        <input
          value={handoffOwner}
          onChange={(event) => onHandoffOwnerChange(event.target.value)}
        />
      </label>

      <div className="rapid-validation-grid">
        {VALIDATION_OPTIONS.map(([label, testId]) => (
          <button
            key={label}
            className={`btn ${validationStatus === label ? 'primary' : 'ghost'} sm`}
            data-testid={testId}
            type="button"
            disabled={validating}
            onClick={() => onValidate(label)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="closeout-validation-meaning" data-testid="closeout-validation-meaning">
        <h4>What each outcome means</h4>
        <ul className="rapid-inline-list">
          {VALIDATION_OPTIONS.map(([label, , meaning]) => (
            <li key={label}>
              <strong>{label}:</strong> {meaning}
            </li>
          ))}
        </ul>
      </div>

      {validationStatus === 'Routed to another process' && routedDestination ? (
        <div className="rapid-note">Destination recorded: {routedDestination}</div>
      ) : null}

      {blocked ? (
        <div className="rapid-note warning" data-testid="closeout-validation-blocked-note">
          {notAccepted ? 'Closeout remains blocked until sponsor validation is recorded.' : null}
          {ownerMissing ? ' Handoff owner is required.' : null}
          {requiredRemaining.length > 0
            ? ` Required checklist items remaining: ${requiredRemaining.join(', ')}.`
            : null}
        </div>
      ) : (
        <div className="rapid-note success" data-testid="closeout-validation-success-note">
          Closeout package can proceed with the current sponsor-validation state.
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.5 Checklist view (visual grouping; original-index testids preserved)
// ---------------------------------------------------------------------------

function ChecklistRow({ item, onToggle }) {
  const nn = String(item.originalIndex + 1).padStart(2, '0');
  const itemSlug = slug(item.item);
  const hasToggle = !NON_TOGGLE_ITEMS.has(item.item);
  return (
    <li className="closeout-checklist-row" data-testid={`closeout-item-${nn}-${itemSlug}`}>
      <div className="row spread">
        <strong>{item.item}</strong>
        <StatusChip status={item.done ? 'done' : 'open'}>{item.done ? 'Done' : 'Open'}</StatusChip>
      </div>
      <div className="rapid-inline-actions">
        {item.requiredForUnblock ? (
          <span className="chip outline sm">Required to unblock</span>
        ) : null}
        {hasToggle ? (
          <button
            className="btn ghost sm"
            data-testid={`closeout-toggle-${nn}-${itemSlug}`}
            type="button"
            onClick={() => onToggle(item.originalIndex)}
          >
            {item.done ? 'Mark open' : 'Mark done'}
          </button>
        ) : (
          <span className="muted small">Handled in Validation view</span>
        )}
      </div>
    </li>
  );
}

export function CloseoutChecklistView({ groups, onToggle }) {
  const renderGroup = (items) =>
    items.map((item) => <ChecklistRow key={item.item} item={item} onToggle={onToggle} />);
  return (
    <section className="rapid-panel closeout-checklist-view" data-testid="closeout-checklist-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Checklist</div>
          <h3>Required gates first, then supporting items</h3>
        </div>
      </div>

      <div className="closeout-checklist-group">
        <div className="closeout-group-head">
          <h4>Required to unblock</h4>
          <span className="chip solid sm">{groups.requiredOpen.length} open</span>
        </div>
        <ul className="rapid-record-list" data-testid="closeout-required-gates">
          {renderGroup(groups.requiredOpen)}
        </ul>
      </div>

      {groups.requiredComplete.length ? (
        <div className="closeout-checklist-group">
          <div className="closeout-group-head">
            <h4>Required and complete</h4>
            <span className="chip outline sm">{groups.requiredComplete.length}</span>
          </div>
          <ul className="rapid-record-list" data-testid="closeout-required-complete">
            {renderGroup(groups.requiredComplete)}
          </ul>
        </div>
      ) : null}

      <div className="closeout-checklist-group">
        <div className="closeout-group-head">
          <h4>Completed / supporting</h4>
          <span className="chip outline sm">{groups.supportingComplete.length}</span>
        </div>
        <ul className="rapid-record-list" data-testid="closeout-supporting-complete">
          {renderGroup(groups.supportingComplete)}
        </ul>
      </div>

      {groups.openNonBlocking.length ? (
        <div className="closeout-checklist-group">
          <div className="closeout-group-head">
            <h4>Open but non-blocking</h4>
            <span className="chip outline sm">{groups.openNonBlocking.length}</span>
          </div>
          <ul className="rapid-record-list" data-testid="closeout-open-nonblocking">
            {renderGroup(groups.openNonBlocking)}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.6 Handoff Packet view
// ---------------------------------------------------------------------------

export function CloseoutHandoffPacketView({
  handoffOwner,
  sustainmentOwner,
  sustainmentCadence,
  carriesForward = [],
}) {
  return (
    <section
      className="rapid-panel closeout-handoff-packet-view"
      data-testid="closeout-handoff-packet-view"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Handoff packet</div>
          <h3>What transfers at handoff</h3>
        </div>
        <span className="chip warn sm" data-testid="handoff-preview-label">
          Preview package
        </span>
      </div>

      <div className="closeout-packet-grid">
        <div className="closeout-packet-block" data-testid="handoff-package-status">
          <h4>Package status</h4>
          <p>Draft / Preview handoff package. Full export is a Preview capability.</p>
        </div>
        <div className="closeout-packet-block" data-testid="handoff-package-owner">
          <h4>Handoff owner</h4>
          <p>{handoffOwner || 'Owner needed'}</p>
        </div>
        <div className="closeout-packet-block" data-testid="handoff-sustainment-owner">
          <h4>Sustainment owner</h4>
          <p>{sustainmentOwner || 'Owner needed'}</p>
          <p className="muted small" data-testid="handoff-sustainment-cadence">
            Cadence: {sustainmentCadence}
          </p>
        </div>
      </div>

      <div className="closeout-carry-forward">
        <h4>What carries forward</h4>
        <ul className="closeout-carry-forward-list" data-testid="handoff-carries-forward">
          {carriesForward.map((entry) => (
            <li key={entry}>
              <span className="chip outline sm">{entry}</span>
            </li>
          ))}
        </ul>
        <p className="muted small">
          Included in the Preview handoff / project carry-forward package.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.7 Graduation & Lineage view (canonical lineage home)
// ---------------------------------------------------------------------------

export function CloseoutGraduationLineageView({ graduation, projectPreview, children }) {
  const projectId = graduation?.projectId;
  return (
    <section
      className="rapid-panel closeout-graduation-lineage-view"
      data-testid="closeout-graduation-lineage-view"
    >
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Graduation &amp; lineage</div>
          <h3>What survives the RAPID</h3>
        </div>
      </div>

      <div className="closeout-graduation-preview" data-testid="closeout-graduation-preview">
        <div className="eyebrow">{graduation?.label}</div>
        <h4 data-testid="closeout-project-preview-id">{projectId}</h4>
        <p>{graduation?.projectTitle}</p>
        <div className="rapid-chip-row">
          <span className="chip outline sm">{graduation?.projectStatus}</span>
        </div>
        {projectPreview?.originNarrative ? (
          <p className="muted small">{projectPreview.originNarrative}</p>
        ) : null}
        {projectId ? (
          <Link
            className="btn primary sm"
            data-testid="closeout-open-project-preview"
            to={`/projects/${projectId}`}
          >
            Open {projectId}
          </Link>
        ) : null}
      </div>

      <div className="closeout-carry-forward-path" data-testid="closeout-carry-forward-path">
        <span className="chip outline sm">Carry-forward path</span>
        <p>RAPID Closeout → Handoff Package → Sustainment Cadence → Project Preview</p>
      </div>

      {/* Canonical lineage home — Governance links here. */}
      <div className="closeout-lineage-wrap" data-testid="closeout-lineage-preview">
        {children}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 7.8 Lessons view
// ---------------------------------------------------------------------------

export function CloseoutLessonsView({ lessons = [] }) {
  return (
    <section className="rapid-panel closeout-lessons-view" data-testid="closeout-lessons-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Lessons</div>
          <h3>Lessons learned</h3>
        </div>
      </div>
      <ul className="rapid-record-list" data-testid="closeout-lessons-list">
        {lessons.map((text) => {
          const id = deriveLessonId(text);
          return (
            <li key={text} data-testid={id ? `closeout-lesson-${id}` : undefined}>
              {text}
            </li>
          );
        })}
      </ul>
      <p className="muted small">
        These lessons are included in the Preview handoff / project carry-forward package.
      </p>
    </section>
  );
}
